import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Show only the last 4 characters of a gift-card code, prefixing the
 * rest as dots so shoulder-surfers and screenshots can't lift the full
 * code from the Active Cards table. Preserves grouping when possible
 * (e.g. "1234-5678-9012-3456" → "•••• •••• •••• 3456").
 */
function maskCode(code) {
  if (!code) return "";
  const raw = String(code);
  const last4 = raw.slice(-4);
  // If the original had dashes, mirror that 4x4 grouping.
  if (raw.includes("-") || raw.length === 16) {
    return `•••• •••• •••• ${last4}`;
  }
  // Fallback: dots + last 4 for shorter/non-standard codes.
  const dotCount = Math.max(4, raw.length - 4);
  return `${"•".repeat(dotCount)}${last4}`;
}

export default function GiftCardsCard() {
  const [cards, setCards] = useState([]);
  const [lookupCode, setLookupCode] = useState("");
  const [lookup, setLookup] = useState(null);
  const [lookupTxns, setLookupTxns] = useState([]);
  const [reloadAmt, setReloadAmt] = useState("");
  const [issueAmt, setIssueAmt] = useState("");
  const [issueName, setIssueName] = useState("");
  const [busy, setBusy] = useState(false);
  // Sold-vs-redeemed report state.
  const [report, setReport] = useState(null);

  const refreshList = () => {
    axios.get(`${API}/gift-cards`, { params: { limit: 100 } })
      .then((r) => setCards(r.data || []))
      .catch(() => toast.error("Couldn't load gift cards"));
  };
  useEffect(() => { refreshList(); }, []);

  // Roll-up report — refresh whenever the tab mounts and after any
  // mutation (issue/reload/redeem/void).
  const refreshReport = () => {
    axios.get(`${API}/gift-cards-report/summary`)
      .then((r) => setReport(r.data))
      .catch(() => { /* non-fatal — report is aux */ });
  };
  useEffect(() => { refreshReport(); }, []);

  const lookupCard = async () => {
    if (!lookupCode.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.get(`${API}/gift-cards/${encodeURIComponent(lookupCode.trim())}`);
      setLookup(data);
      const t = await axios.get(`${API}/gift-cards/${encodeURIComponent(lookupCode.trim())}/transactions`);
      setLookupTxns(t.data || []);
    } catch (err) {
      setLookup(null);
      setLookupTxns([]);
      toast.error(err.response?.data?.detail || "Not found");
    } finally {
      setBusy(false);
    }
  };

  const doReload = async () => {
    const amount = parseFloat(reloadAmt);
    if (!(amount > 0)) { toast.error("Enter an amount > $0"); return; }
    if (!lookup?.code) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/gift-cards/${lookup.code}/reload`, { amount });
      setLookup(data);
      setReloadAmt("");
      const t = await axios.get(`${API}/gift-cards/${lookup.code}/transactions`);
      setLookupTxns(t.data || []);
      refreshList();
      refreshReport();
      toast.success(`Reloaded — new balance $${data.current_balance?.toFixed(2)}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reload failed");
    } finally {
      setBusy(false);
    }
  };

  const doVoid = async () => {
    if (!lookup?.code) return;
    if (!window.confirm("Void this card? Balance will be zeroed and it can no longer be redeemed or reloaded.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/gift-cards/${lookup.code}/void`);
      lookupCard();
      refreshList();
      refreshReport();
      toast.success("Card voided");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Void failed");
    } finally {
      setBusy(false);
    }
  };

  const doIssue = async () => {
    const amount = parseFloat(issueAmt);
    if (!(amount > 0)) { toast.error("Enter an amount > $0"); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/gift-cards`, {
        amount,
        recipient_name: issueName.trim() || null,
      });
      setIssueAmt("");
      setIssueName("");
      setLookupCode(data.code);
      setLookup(data);
      refreshList();
      refreshReport();
      toast.success(`Issued — code ${data.code}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Issue failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="gift-cards-card">
      {/* Sold vs Redeemed roll-up — day / week / month / lifetime */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <CardTitle>Gift Card Report</CardTitle>
              <CardDescription>Sold vs redeemed across time windows, plus outstanding liability.</CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  import("@/utils/downloadCsv").then((m) => {
                    m.downloadCsv(`${API}/gift-cards-report/export.csv`);
                  });
                }}
                className="text-xs text-primary hover:underline"
                data-testid="gc-report-export-csv"
              >
                Export CSV
              </button>
              <Button variant="ghost" size="sm" onClick={refreshReport} data-testid="gc-report-refresh">Refresh</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!report ? (
            <p className="text-sm text-muted-foreground">Loading report…</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="gc-report-tiles">
                {["today", "week", "month", "lifetime"].map((k) => (
                  <div key={k} className="rounded-lg bg-secondary/40 p-3 space-y-1" data-testid={`gc-report-${k}`}>
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      {k === "today" ? "Today" : k === "week" ? "Last 7 days" : k === "month" ? "Last 30 days" : "All time"}
                    </p>
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-muted-foreground">Sold</span>
                      <span className="font-heading text-base">${report[k].sold.toFixed(2)}</span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-muted-foreground">Reload</span>
                      <span className="text-sm">${report[k].reloaded.toFixed(2)}</span>
                    </div>
                    <div className="flex items-baseline justify-between">
                      <span className="text-xs text-muted-foreground">Redeemed</span>
                      <span className="text-sm text-destructive">−${report[k].redeemed.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-border pt-1 flex items-baseline justify-between">
                      <span className="text-xs font-medium">Net</span>
                      <span className={`text-sm font-medium ${report[k].net >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}`}>
                        {report[k].net >= 0 ? "+" : ""}${report[k].net.toFixed(2)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between rounded-lg border-2 border-amber-300/50 dark:border-amber-800/50 bg-amber-50/50 dark:bg-amber-950/30 p-3">
                <div>
                  <p className="text-sm font-medium">Outstanding liability</p>
                  <p className="text-xs text-muted-foreground">Cash you still owe customers via active cards ({report.active_card_count} active)</p>
                </div>
                <p className="font-heading text-2xl text-amber-700 dark:text-amber-400" data-testid="gc-report-liability">
                  ${report.outstanding_liability.toFixed(2)}
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Issue new card */}
        <Card>
          <CardHeader>
            <CardTitle>Issue new gift card</CardTitle>
            <CardDescription>Auto-generate a 16-digit e-gift code for immediate sale.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-2">
              <Label>Amount ($)</Label>
              <Input type="number" min="0.01" step="0.01" value={issueAmt} onChange={(e) => setIssueAmt(e.target.value)} placeholder="25.00" data-testid="gc-issue-amount" />
            </div>
            <div className="space-y-2">
              <Label>Recipient name (optional)</Label>
              <Input value={issueName} onChange={(e) => setIssueName(e.target.value)} placeholder="For Alex" data-testid="gc-issue-name" />
            </div>
            <Button onClick={doIssue} disabled={busy} data-testid="gc-issue-btn">Issue card</Button>
          </CardContent>
        </Card>

        {/* Lookup / reload */}
        <Card>
          <CardHeader>
            <CardTitle>Load / Reload existing card</CardTitle>
            <CardDescription>Look up a card by code, then add balance or void.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={lookupCode}
                onChange={(e) => setLookupCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") lookupCard(); }}
                placeholder="Scan or type card code…"
                className="font-mono"
                data-testid="gc-lookup-code"
              />
              <Button variant="secondary" onClick={lookupCard} disabled={busy || !lookupCode.trim()} data-testid="gc-lookup-btn">
                Look up
              </Button>
            </div>

            {lookup && (
              <div className="rounded-lg border p-3 space-y-2" data-testid="gc-detail">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium font-mono">{lookup.code}</p>
                    {lookup.recipient_name && <p className="text-xs text-muted-foreground">for {lookup.recipient_name}</p>}
                  </div>
                  <Badge variant={lookup.status === "voided" ? "destructive" : lookup.status === "depleted" ? "secondary" : "default"}>
                    {lookup.status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Balance</span>
                  <span className="font-heading text-lg" data-testid="gc-balance">${(lookup.current_balance ?? 0).toFixed(2)}</span>
                </div>
                {lookup.status !== "voided" && (
                  <div className="flex gap-2 pt-2">
                    <Input type="number" min="0.01" step="0.01" value={reloadAmt} onChange={(e) => setReloadAmt(e.target.value)} placeholder="Add ($)" data-testid="gc-reload-amount" />
                    <Button onClick={doReload} disabled={busy} data-testid="gc-reload-btn">Reload</Button>
                    <Button variant="ghost" className="text-destructive" onClick={doVoid} disabled={busy} data-testid="gc-void-btn">Void</Button>
                  </div>
                )}
                {lookupTxns.length > 0 && (
                  <details className="pt-2">
                    <summary className="text-xs text-primary cursor-pointer">Transactions ({lookupTxns.length})</summary>
                    <ul className="text-xs mt-2 space-y-1">
                      {lookupTxns.map((t) => (
                        <li key={t.id} className="flex justify-between gap-3">
                          <span>{new Date(t.created_at).toLocaleString()}</span>
                          <span className="capitalize text-muted-foreground">{t.kind}</span>
                          <span className={t.kind === "redeem" ? "text-destructive" : ""}>
                            {t.kind === "redeem" ? "−" : "+"}${t.amount.toFixed(2)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Active cards</CardTitle>
              <CardDescription>Most recent 100 activated cards.</CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={refreshList} data-testid="gc-refresh">Refresh</Button>
          </div>
        </CardHeader>
        <CardContent>
          {cards.length === 0 ? (
            <p className="text-sm text-muted-foreground">No cards issued yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="text-left py-1">Code</th>
                  <th className="text-left py-1">Recipient</th>
                  <th className="text-right py-1">Initial</th>
                  <th className="text-right py-1">Balance</th>
                  <th className="text-left py-1">Status</th>
                </tr>
              </thead>
              <tbody>
                {cards.map((c) => (
                  <tr key={c.id} className="border-t border-border cursor-pointer hover:bg-secondary/30"
                      onClick={() => {
                        // Auto-open the detail card so managers can act on a
                        // single click instead of type-then-Look-Up.
                        setLookupCode(c.code);
                        setLookup(c);
                        axios.get(`${API}/gift-cards/${encodeURIComponent(c.code)}/transactions`)
                          .then((r) => setLookupTxns(r.data || []))
                          .catch(() => setLookupTxns([]));
                      }}
                      data-testid={`gc-row-${c.code}`}>
                    <td className="py-1 font-mono" data-testid={`gc-code-masked-${c.code}`}>{maskCode(c.code)}</td>
                    <td className="py-1 text-muted-foreground">{c.recipient_name || "—"}</td>
                    <td className="py-1 text-right">${(c.initial_balance ?? 0).toFixed(2)}</td>
                    <td className="py-1 text-right font-medium">${(c.current_balance ?? 0).toFixed(2)}</td>
                    <td className="py-1 capitalize text-xs">{c.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
